'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function IrrCalculator() {
  const t = useTranslations("tools.irrCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Fixed Cash Flow State
  const [fixedInputs, setFixedInputs] = useState({
    initialInvestment: 10000,
    holdingYears: 2,
    holdingMonths: 6,
    endingBalance: 15000,
    periodicType: 'withdraw',
    periodicAmount: 100,
    periodicFrequency: 1,
    paymentTiming: 'end'
  });
  const [fixedResults, setFixedResults] = useState<{ irr: number } | null>(null);

  // Irregular Cash Flow State
  const [irregularInputs, setIrregularInputs] = useState({
    initialInvestment: 50000,
    cashFlows: [-10000, 30000, 50000, '', '', '', '', '']
  });
  const [irregularResults, setIrregularResults] = useState<{ irr: number; npv: number } | null>(null);
  const [showMoreYears, setShowMoreYears] = useState(false);
  const [extraCashFlows, setExtraCashFlows] = useState<string[]>(Array(12).fill(''));

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // IRR Calculation Functions
  const calculateNPV = (rate: number, cashFlows: number[]): number => {
    let npv = cashFlows[0];
    for (let i = 1; i < cashFlows.length; i++) {
      npv += cashFlows[i] / Math.pow(1 + rate, i);
    }
    return npv;
  };

  const calculateIRR = (cashFlows: number[], guess: number = 0.1): number => {
    const maxIterations = 1000;
    const tolerance = 0.000001;
    let rate = guess;

    for (let i = 0; i < maxIterations; i++) {
      const npv = calculateNPV(rate, cashFlows);

      if (Math.abs(npv) < tolerance) {
        return rate;
      }

      let derivative = 0;
      for (let j = 1; j < cashFlows.length; j++) {
        derivative -= j * cashFlows[j] / Math.pow(1 + rate, j + 1);
      }

      if (Math.abs(derivative) < tolerance) {
        break;
      }

      rate = rate - npv / derivative;
    }

    return rate;
  };

  // Fixed Cash Flow Calculator
  const handleFixedCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const totalMonths = fixedInputs.holdingYears * 12 + fixedInputs.holdingMonths;
    const periods = totalMonths / fixedInputs.periodicFrequency;

    const cashFlows = [-fixedInputs.initialInvestment];

    for (let i = 1; i <= periods; i++) {
      let cashFlow = 0;
      if (fixedInputs.periodicType === 'withdraw') {
        cashFlow = fixedInputs.periodicAmount;
      } else {
        cashFlow = -fixedInputs.periodicAmount;
      }

      if (fixedInputs.paymentTiming === 'beginning' && i === 1) {
        cashFlows[0] += cashFlow;
      } else {
        cashFlows.push(cashFlow);
      }
    }

    cashFlows[cashFlows.length - 1] += fixedInputs.endingBalance;

    try {
      const irr = calculateIRR(cashFlows);
      const annualIRR = Math.pow(1 + irr, 12 / fixedInputs.periodicFrequency) - 1;
      setFixedResults({ irr: annualIRR });
    } catch (error) {
      console.error(error);
      setFixedResults({ irr: NaN });
    }
  };

  // Irregular Cash Flow Calculator
  const handleIrregularCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const cashFlows = [-irregularInputs.initialInvestment];

    irregularInputs.cashFlows.forEach(value => {
      if (value !== '') {
        cashFlows.push(parseFloat(value as string));
      }
    });

    if (showMoreYears) {
      extraCashFlows.forEach(value => {
        if (value !== '') {
          cashFlows.push(parseFloat(value));
        }
      });
    }

    if (cashFlows.length < 2) {
      alert(t('errorMinCashFlow'));
      return;
    }

    try {
      const irr = calculateIRR(cashFlows);
      const npv = calculateNPV(irr, cashFlows);
      setIrregularResults({ irr, npv });
    } catch (error) {
      console.error(error);
      setIrregularResults({ irr: NaN, npv: NaN });
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const calculatorSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { color: '#2563eb', marginBottom: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const sectionDescriptionStyle = {
    color: '#64748b',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
    fontSize: '0.95rem',
  };

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#334155',
    fontWeight: 500,
    fontSize: '0.95rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '0.875rem' }
  );

  const selectStyle = { ...inputStyle };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const radioGroupStyle = {
    display: 'flex',
    gap: '2rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap' as const,
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontWeight: 400,
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const clearBtnStyle = {
    ...calculateBtnStyle,
    background: '#f1f5f9',
    color: '#475569',
  };

  const cashFlowInputsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const cashFlowItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const cashFlowLabelStyle = {
    minWidth: '80px',
    marginBottom: 0,
    fontSize: '0.9rem',
  };

  const showMoreBtnStyle = {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '0.5rem',
    color: '#2563eb',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.3s ease',
  };

  const resultsStyle = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '1rem',
    padding: '2rem',
    marginTop: '2rem',
    border: '2px solid #86efac',
  };

  const resultsH3Style = getResponsiveStyle(
    { color: '#15803d', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  };

  const resultLabelStyle = {
    color: '#475569',
    fontWeight: 500,
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#15803d' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = {
    ...paragraphStyle,
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    color: colors.primary,
    fontWeight: 600,
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          {/* Fixed Cash Flow Calculator */}
          <div style={calculatorSectionStyle}>
            <h3 style={sectionTitleStyle}>💰 {t('fixedCashFlowTitle')}</h3>
            <p style={sectionDescriptionStyle}>{t('fixedCashFlowDescription')}</p>

            <form onSubmit={handleFixedCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="initial-investment">{t('initialInvestmentLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="initial-investment"
                  step="0.01"
                  value={fixedInputs.initialInvestment}
                  onChange={(e) => setFixedInputs({ ...fixedInputs, initialInvestment: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="holding-years">{t('holdingYearsLabel')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="holding-years"
                    min="0"
                    value={fixedInputs.holdingYears}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, holdingYears: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="holding-months">{t('holdingMonthsLabel')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="holding-months"
                    min="0"
                    max="11"
                    value={fixedInputs.holdingMonths}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, holdingMonths: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="ending-balance">{t('endingBalanceLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="ending-balance"
                  step="0.01"
                  value={fixedInputs.endingBalance}
                  onChange={(e) => setFixedInputs({ ...fixedInputs, endingBalance: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="periodic-type">{t('periodicTypeLabel')}</label>
                  <select
                    style={selectStyle}
                    id="periodic-type"
                    value={fixedInputs.periodicType}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, periodicType: e.target.value })}
                  >
                    <option value="deposit">{t('depositOption')}</option>
                    <option value="withdraw">{t('withdrawOption')}</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="periodic-amount">{t('periodicAmountLabel')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="periodic-amount"
                    step="0.01"
                    value={fixedInputs.periodicAmount}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, periodicAmount: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="periodic-frequency">{t('frequencyLabel')}</label>
                <select
                  style={selectStyle}
                  id="periodic-frequency"
                  value={fixedInputs.periodicFrequency}
                  onChange={(e) => setFixedInputs({ ...fixedInputs, periodicFrequency: parseFloat(e.target.value) })}
                >
                  <option value="12">{t('annuallyOption')}</option>
                  <option value="6">{t('semiannuallyOption')}</option>
                  <option value="4">{t('quarterlyOption')}</option>
                  <option value="1">{t('monthlyOption')}</option>
                  <option value="0.5">{t('semimonthlyOption')}</option>
                  <option value="0.23">{t('biweeklyOption')}</option>
                  <option value="0.115">{t('weeklyOption')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('paymentTimingLabel')}</label>
                <div style={radioGroupStyle}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="payment-timing"
                      value="beginning"
                      checked={fixedInputs.paymentTiming === 'beginning'}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, paymentTiming: e.target.value })}
                    />
                    {t('beginningOption')}
                  </label>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="payment-timing"
                      value="end"
                      checked={fixedInputs.paymentTiming === 'end'}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, paymentTiming: e.target.value })}
                    />
                    {t('endOption')}
                  </label>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button
                  type="button"
                  style={clearBtnStyle}
                  onClick={() => {
                    setFixedInputs({
                      initialInvestment: 10000,
                      holdingYears: 2,
                      holdingMonths: 6,
                      endingBalance: 15000,
                      periodicType: 'withdraw',
                      periodicAmount: 100,
                      periodicFrequency: 1,
                      paymentTiming: 'end'
                    });
                    setFixedResults(null);
                  }}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>

            {fixedResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('irrLabel')}</span>
                  <span style={resultValueStyle}>
                    {isNaN(fixedResults.irr) ? t('unableToCalculate') : `${(fixedResults.irr * 100).toFixed(2)}%`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Irregular Cash Flow Calculator */}
          <div style={calculatorSectionStyle}>
            <h3 style={sectionTitleStyle}>📈 {t('irregularCashFlowTitle')}</h3>
            <p style={sectionDescriptionStyle}>{t('irregularCashFlowDescription')}</p>

            <form onSubmit={handleIrregularCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="initial-investment-2">{t('initialInvestmentLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="initial-investment-2"
                  step="0.01"
                  value={irregularInputs.initialInvestment}
                  onChange={(e) => setIrregularInputs({ ...irregularInputs, initialInvestment: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('annualCashFlowsLabel')}</label>
                <div style={cashFlowInputsStyle}>
                  {irregularInputs.cashFlows.map((value, index) => (
                    <div key={index} style={cashFlowItemStyle}>
                      <label style={cashFlowLabelStyle}>{t('yearLabel', { year: index + 1 })}</label>
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={(e) => {
                          const newCashFlows = [...irregularInputs.cashFlows];
                          newCashFlows[index] = e.target.value;
                          setIrregularInputs({ ...irregularInputs, cashFlows: newCashFlows });
                        }}
                      />
                    </div>
                  ))}
                </div>

                {showMoreYears && (
                  <div style={{ ...cashFlowInputsStyle, marginTop: '1rem' }}>
                    {extraCashFlows.map((value, index) => (
                      <div key={index} style={cashFlowItemStyle}>
                        <label style={cashFlowLabelStyle}>{t('yearLabel', { year: index + 9 })}</label>
                        <input
                          style={{ ...inputStyle, flex: 1 }}
                          type="number"
                          step="0.01"
                          value={value}
                          onChange={(e) => {
                            const newExtraCashFlows = [...extraCashFlows];
                            newExtraCashFlows[index] = e.target.value;
                            setExtraCashFlows(newExtraCashFlows);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  style={showMoreBtnStyle}
                  onClick={() => setShowMoreYears(!showMoreYears)}
                >
                  {showMoreYears ? t('hideExtraYears') : t('showMoreYears')}
                </button>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button
                  type="button"
                  style={clearBtnStyle}
                  onClick={() => {
                    setIrregularInputs({
                      initialInvestment: 50000,
                      cashFlows: [-10000, 30000, 50000, '', '', '', '', '']
                    });
                    setExtraCashFlows(Array(12).fill(''));
                    setIrregularResults(null);
                  }}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>

            {irregularResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('irrLabel')}</span>
                  <span style={resultValueStyle}>
                    {isNaN(irregularResults.irr) ? t('unableToCalculate') : `${(irregularResults.irr * 100).toFixed(2)}%`}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('npvLabel')}</span>
                  <span style={resultValueStyle}>
                    {isNaN(irregularResults.npv) ? 'N/A' : `$${irregularResults.npv.toFixed(2)}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://projectmanagementacademy.net/resources/blog/internal-rate-of-return/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.wallstreetprep.com/knowledge/irr-internal-rate-of-return/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://en.wikipedia.org/wiki/Internal_rate_of_return" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.moonfare.com/glossary/internal-rate-of-return-irr" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.jpmorgan.com/insights/real-estate/commercial-term-lending/what-is-internal-rate-of-return-in-commercial-real-estate" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://corporatefinanceinstitute.com/resources/valuation/internal-rate-return-irr/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}